import TinyColor from "@ctrl/tinycolor";
import { useLoad } from "@redotech/react-util/load";
import { BrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import {
  CustomFieldType,
  InputElement,
} from "@redotech/redo-content-builder-common/element-schema";
import { BrandKitOverrides } from "@redotech/redo-marketing-common/signup-form/signup-form-schema";
import { smsLegalDisclaimerParts } from "@redotech/redo-marketing-common/sms-legal-disclaimer";
import { supportedCountryCodes } from "@redotech/redo-web/arbiter-components/input/phone-number-country";
import { BrandKitDateInput } from "@redotech/redo-web/brand-kit-date-input";
import { BrandKitDropdownInput } from "@redotech/redo-web/brand-kit-dropdown-input";
import { BrandKitRadioGroup } from "@redotech/redo-web/brand-kit-radio-group";
import {
  BrandKitPhoneNumberInput,
  BrandKitTextAreaInput,
  BrandKitTextInput,
} from "@redotech/redo-web/brand-kit-text-input";
import { ExternalLink } from "@redotech/redo-web/link";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import classNames from "classnames";
import React, { memo, NamedExoticComponent, useContext, useState } from "react";
import * as signupFormCss from "../signup-form.module.css";
import {
  childContrastBackgroundColor,
  getBaseElementStyles,
} from "./base-element-styles";
import { CustomFieldContext } from "./context";
import { ElementBox } from "./element-box";
import { FormElementProps } from "./index";

const EmailInput = memo(function EmailInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      inputTypeHint="email"
      name="email"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const PhoneInput = memo(function PhoneInput({
  brandKitOverrides,
  brandKit,
  backgroundColor,
  element,
  isBuilder,
  merchantName,
  supportEmail,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  const disclaimerParts = smsLegalDisclaimerParts<React.ReactNode>(
    {
      renderText: (text) => text,
      renderLink: (text, url) => (
        <ExternalLink key={url} showIcon={false} url={url}>
          {text}
        </ExternalLink>
      ),
    },
    { brandName: merchantName, supportEmail },
  );
  return (
    <>
      <BrandKitPhoneNumberInput
        brandKit={brandKit}
        elementOverride={element.styleOverride}
        name="phone"
        override={
          brandKitOverrides?.inputs.enabled
            ? brandKitOverrides?.inputs
            : undefined
        }
        required={!element.optional}
        setValue={setValue}
        state={isBuilder ? "disabled" : undefined}
        supportedCountryCodes={supportedCountryCodes}
        value={value}
      />
      <Text
        className={classNames(
          signupFormCss.legalDisclaimer,
          TinyColor(backgroundColor).isDark()
            ? signupFormCss.invertedTextColor
            : undefined,
        )}
        dir="column"
        mt="lg"
      >
        {disclaimerParts.map((part, i) => (
          <React.Fragment key={i}>{part}</React.Fragment>
        ))}
      </Text>
    </>
  );
});

const FirstNameInput = memo(function FirstNameInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name="firstName"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const LastNameInput = memo(function LastNameInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name="lastName"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const FullNameInput = memo(function FullNameInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name="name"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const BirthdayInput = memo(function BirthdayInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name="birthday"
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomDateField = memo(function CustomDateField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<Temporal.PlainDate | null>(null);
  return (
    <BrandKitDateInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomDropdownField = memo(function CustomDropdownField({
  brandKitOverrides,
  element,
  brandKit,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const getOptionsForField = useContext(CustomFieldContext);
  const [value, setValue] = useState<string | null>(null);

  const fieldOptions = useLoad(async () => {
    if (element.customField?.id && getOptionsForField) {
      const result = await getOptionsForField(element.customField.id);
      return result.options;
    }
    return undefined;
  }, [element.customField?.id, getOptionsForField]);

  return (
    <BrandKitDropdownInput
      brandKit={brandKit}
      disabled={isBuilder}
      elementOverride={element.styleOverride}
      name={element.customField?.id}
      options={fieldOptions.value ?? []}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={(value) => setValue(value)}
      value={value}
    />
  );
});

const CustomNumberField = memo(function CustomNumberField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      htmlPattern="^[0-9]*$"
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={(newValue) => {
        if (newValue === "" || /^[0-9]*$/.test(newValue)) {
          setValue(newValue);
        }
      }}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomLongTextField = memo(function CustomLongTextField({
  brandKitOverrides,
  element,
  brandKit,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextAreaInput
      brandKit={brandKit}
      disabled={isBuilder}
      elementOverride={element.styleOverride}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      value={value}
    />
  );
});

const CustomTextShortField = memo(function CustomTextShortField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<string>("");
  return (
    <BrandKitTextInput
      brandKit={brandKit}
      elementOverride={element.styleOverride}
      name={element.customField?.id}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      placeholder={element.placeholderText}
      required={!element.optional}
      setValue={setValue}
      state={isBuilder ? "disabled" : undefined}
      value={value}
    />
  );
});

const CustomYesNoField = memo(function CustomYesNoField({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: {
  brandKitOverrides?: BrandKitOverrides;
  element: InputElement;
  brandKit: BrandKit;
  isBuilder?: boolean;
}) {
  const [value, setValue] = useState<boolean>(true);
  return (
    <BrandKitRadioGroup
      brandKit={brandKit}
      disabled={isBuilder}
      elementOverride={element.styleOverride}
      name={element.customField?.id}
      options={[
        { id: true, label: "Yes" },
        { id: false, label: "No" },
      ]}
      override={
        brandKitOverrides?.inputs.enabled
          ? brandKitOverrides?.inputs
          : undefined
      }
      required={!element.optional}
      setValue={(value) => typeof value === "boolean" && setValue(value)}
      value={value}
    />
  );
});

const CustomInput = memo(function CustomInput({
  brandKitOverrides,
  brandKit,
  element,
  isBuilder,
}: FormElementProps<InputElement>) {
  if (!element.customField?.fieldType) {
    // A custom field dropped from the palette has no field picked yet. The
    // builder still needs something visible to click on to open the inspector;
    // on the storefront there is nothing to collect.
    return isBuilder ? (
      <CustomTextShortField
        brandKit={brandKit}
        brandKitOverrides={brandKitOverrides}
        element={element}
        isBuilder={isBuilder}
      />
    ) : null;
  }

  switch (element.customField.fieldType) {
    case CustomFieldType.Date:
      return (
        <CustomDateField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.Dropdown:
      return (
        <CustomDropdownField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.Number:
      return (
        <CustomNumberField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.TextLong:
      return (
        <CustomLongTextField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.TextShort:
      return (
        <CustomTextShortField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    case CustomFieldType.YesNoQuestion:
      return (
        <CustomYesNoField
          brandKit={brandKit}
          brandKitOverrides={brandKitOverrides}
          element={element}
          isBuilder={isBuilder}
        />
      );
    default:
      assertNever(element.customField.fieldType);
  }
});

const componentMap: Record<
  InputElement["inputType"],
  NamedExoticComponent<FormElementProps<InputElement>>
> = {
  email: EmailInput,
  phone: PhoneInput,
  birthday: BirthdayInput,
  firstName: FirstNameInput,
  lastName: LastNameInput,
  fullName: FullNameInput,
  custom: CustomInput,
};

export const Input = memo(function Input(
  props: FormElementProps<InputElement>,
) {
  const { element } = props;
  const Component = componentMap[element.inputType];

  if (!Component) {
    return null;
  }

  const styles = getBaseElementStyles(element);
  const hasStyles = Object.keys(styles).length > 0;
  const backgroundColor = childContrastBackgroundColor(
    props.backgroundColor,
    element.backgroundColor,
  );

  return (
    <ElementBox
      alignment={element.contentAlignment}
      bare={!hasStyles}
      fillsWidth
      style={{
        // design-allow: merchant-authored element styles have no tokens
        ...styles,
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
      unclippedWhileHugging
    >
      <Component {...props} backgroundColor={backgroundColor} />
    </ElementBox>
  );
});
